/**
 * UniversalFeedbackButton — Cross-venture feedback widget for all UITL products.
 *
 * Works on public pages (anonymous + optional email) and authenticated pages (admin auto-fix).
 * Returns a tracking link after submission so users can follow their feedback's progress.
 *
 * Part of the autonomous feedback triage & self-healing system.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';

// ─── Types ───

export type FeedbackType = 'bug' | 'feature' | 'feedback';

export interface FeedbackUser {
  id?: string | number;
  email?: string;
  is_admin?: boolean;
}

export interface UniversalFeedbackButtonProps {
  /** Venture identifier */
  venture: string;
  /** API base URL for feedback submission (e.g., https://api.dubblebooked.com) */
  apiEndpoint: string;
  /** Current user info (null for anonymous/public pages) */
  user?: FeedbackUser | null;
  /** Show email capture field (for public pages) */
  showEmailCapture?: boolean;
  /** Show auto-fix toggle (admin only) */
  showAutoFix?: boolean;
  /** Current page path */
  currentPath?: string;
  /** Primary color for theming */
  primaryColor?: string;
  /** FAB position */
  position?: 'bottom-right' | 'bottom-left';
  /** Bottom offset in px (default 24) */
  bottomOffset?: number;
  /** Callback with tracking URL after submission */
  onTrackingLink?: (url: string) => void;
  /** Custom submit handler (overrides default API call) */
  onSubmit?: (data: FeedbackSubmitData) => Promise<FeedbackSubmitResult>;
  /** Feedback types to show (default: bug, feature) */
  feedbackTypes?: FeedbackType[];
}

export interface FeedbackSubmitData {
  type: string;
  title: string;
  description: string | null;
  page: string;
  source_user_email?: string;
  auto_fix: boolean;
  user_context: {
    navigation_history: string[];
    browser_info: string;
    window_size: string;
  };
}

export interface FeedbackSubmitResult {
  id: number;
  tracking_token?: string;
  tracking_url?: string;
}

// ─── Styles (inline, no external deps) ───

const STYLES = {
  fab: (color: string, position: 'bottom-right' | 'bottom-left', bottom: number) => ({
    position: 'fixed' as const,
    [position === 'bottom-right' ? 'right' : 'left']: 24,
    bottom,
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: color,
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 99999,
    transition: 'transform 0.2s, box-shadow 0.2s',
  }),
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 100000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialog: {
    backgroundColor: '#1e293b',
    borderRadius: 12,
    padding: '24px',
    width: '90%',
    maxWidth: 480,
    maxHeight: '90vh',
    overflow: 'auto' as const,
    color: '#e2e8f0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  title: {
    margin: '0 0 16px',
    fontSize: 18,
    fontWeight: 600,
    color: '#f1f5f9',
  },
  typeButton: (active: boolean, color: string) => ({
    padding: '8px 16px',
    border: `1px solid ${active ? color : '#334155'}`,
    borderRadius: 8,
    backgroundColor: active ? `${color}20` : 'transparent',
    color: active ? color : '#94a3b8',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    transition: 'all 0.15s',
  }),
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 14,
    outline: 'none',
    marginBottom: 12,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 14,
    outline: 'none',
    marginBottom: 12,
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    minHeight: 80,
    boxSizing: 'border-box' as const,
  },
  submitButton: (color: string, disabled: boolean) => ({
    padding: '10px 24px',
    backgroundColor: disabled ? '#334155' : color,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 14,
    fontWeight: 600,
    opacity: disabled ? 0.5 : 1,
  }),
  cancelButton: {
    padding: '10px 24px',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    border: '1px solid #334155',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
  },
  label: {
    display: 'block',
    fontSize: 12,
    color: '#94a3b8',
    marginBottom: 4,
    fontWeight: 500,
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    padding: '8px 0',
  },
  trackingBox: {
    backgroundColor: '#0f172a',
    border: '1px solid #4F46E5',
    borderRadius: 8,
    padding: '12px 16px',
    marginTop: 16,
    textAlign: 'center' as const,
  },
  trackingLink: (color: string) => ({
    color,
    fontWeight: 600,
    textDecoration: 'none',
  }),
};

// ─── SVG Icons (inline, no MUI dependency) ───

const FeedbackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Component ───

export const UniversalFeedbackButton: React.FC<UniversalFeedbackButtonProps> = ({
  venture,
  apiEndpoint,
  user = null,
  showEmailCapture = false,
  showAutoFix = false,
  currentPath,
  primaryColor = '#4F46E5',
  position = 'bottom-right',
  bottomOffset = 24,
  onTrackingLink,
  onSubmit,
  feedbackTypes = ['bug', 'feature'],
}) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>(feedbackTypes[0] || 'bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [autoFix, setAutoFix] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navHistoryRef = useRef<string[]>([]);

  // Track navigation history
  useEffect(() => {
    if (currentPath) {
      navHistoryRef.current = [
        currentPath,
        ...navHistoryRef.current.filter((p) => p !== currentPath),
      ].slice(0, 5);
    }
  }, [currentPath]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setType(feedbackTypes[0] || 'bug');
    setTitle('');
    setDescription('');
    setAutoFix(false);
    setSubmitted(false);
    setTrackingUrl(null);
    setError(null);
  }, [feedbackTypes]);

  const handleClose = useCallback(() => {
    if (!submitting) setOpen(false);
  }, [submitting]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    setError(null);

    const data: FeedbackSubmitData = {
      type,
      title: title.trim(),
      description: description.trim() || null,
      page: currentPath || window.location.pathname,
      auto_fix: autoFix,
      source_user_email: email.trim() || undefined,
      user_context: {
        navigation_history: navHistoryRef.current,
        browser_info: navigator.userAgent,
        window_size: `${window.innerWidth}x${window.innerHeight}`,
      },
    };

    try {
      let result: FeedbackSubmitResult;
      if (onSubmit) {
        result = await onSubmit(data);
      } else {
        const resp = await fetch(`${apiEndpoint}/api/v1/feedback/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        });
        if (!resp.ok) throw new Error(`Submit failed: ${resp.status}`);
        result = await resp.json();
      }

      setSubmitted(true);
      if (result.tracking_url) {
        setTrackingUrl(result.tracking_url);
        onTrackingLink?.(result.tracking_url);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  }, [type, title, description, email, autoFix, currentPath, apiEndpoint, onSubmit, onTrackingLink]);

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        style={STYLES.fab(primaryColor, position, bottomOffset)}
        aria-label="Submit feedback"
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <FeedbackIcon />
      </button>
    );
  }

  return (
    <>
      {/* FAB (keep it visible behind overlay) */}
      <button
        style={{ ...STYLES.fab(primaryColor, position, bottomOffset), opacity: 0.5 }}
        aria-hidden
      >
        <FeedbackIcon />
      </button>

      {/* Overlay + Dialog */}
      <div style={STYLES.overlay} onClick={handleClose}>
        <div style={STYLES.dialog} onClick={(e) => e.stopPropagation()}>
          {submitted ? (
            // ─── Success State ───
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ color: '#22c55e', marginBottom: 12 }}>
                <CheckIcon />
              </div>
              <h3 style={{ ...STYLES.title, color: '#22c55e' }}>Thank you!</h3>
              <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 8px' }}>
                {autoFix
                  ? 'Submitted — auto-fix queued for Patch McFixitface'
                  : 'Your feedback has been received. We review every submission.'}
              </p>
              {trackingUrl && (
                <div style={STYLES.trackingBox}>
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 8px' }}>
                    Track your feedback's progress:
                  </p>
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={STYLES.trackingLink(primaryColor)}
                  >
                    View Status
                  </a>
                </div>
              )}
              <button
                onClick={handleClose}
                style={{ ...STYLES.cancelButton, marginTop: 16 }}
              >
                Close
              </button>
            </div>
          ) : (
            // ─── Form State ───
            <>
              <h3 style={STYLES.title}>Send Feedback</h3>

              {/* Type selector */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {feedbackTypes.map((ft) => (
                  <button
                    key={ft}
                    onClick={() => setType(ft)}
                    style={STYLES.typeButton(type === ft, primaryColor)}
                  >
                    {ft === 'bug' ? '🐛 Bug' : ft === 'feature' ? '💡 Feature' : '💬 Feedback'}
                  </button>
                ))}
              </div>

              {/* Title */}
              <label style={STYLES.label}>Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 255))}
                placeholder={type === 'bug' ? 'What went wrong?' : 'What would you like?'}
                style={STYLES.input}
                maxLength={255}
                autoFocus
              />

              {/* Description */}
              <label style={STYLES.label}>
                {type === 'bug' ? 'Steps to reproduce (optional)' : 'Details (optional)'}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={type === 'bug' ? 'What happened? What did you expect?' : 'Any additional context...'}
                style={STYLES.textarea}
                rows={3}
              />

              {/* Email capture (for public/anonymous pages) */}
              {(showEmailCapture && !user) && (
                <>
                  <label style={STYLES.label}>Email (optional — for status updates)</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    style={STYLES.input}
                  />
                </>
              )}

              {/* Auto-fix toggle (admin only) */}
              {showAutoFix && user?.is_admin && (
                <div style={STYLES.switchRow}>
                  <input
                    type="checkbox"
                    checked={autoFix}
                    onChange={(e) => setAutoFix(e.target.checked)}
                    id="auto-fix-toggle"
                    style={{ accentColor: primaryColor }}
                  />
                  <label htmlFor="auto-fix-toggle" style={{ fontSize: 13, color: autoFix ? primaryColor : '#94a3b8', fontWeight: autoFix ? 600 : 400, cursor: 'pointer' }}>
                    ⚡ Route to Patch McFixitface (auto-triage)
                  </label>
                </div>
              )}

              {/* Error */}
              {error && (
                <p style={{ color: '#ef4444', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button onClick={handleClose} style={STYLES.cancelButton}>
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!title.trim() || submitting}
                  style={STYLES.submitButton(primaryColor, !title.trim() || submitting)}
                >
                  {submitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default UniversalFeedbackButton;
